import { SFXAudio } from '@/config/AudioConfig'
import AudioEngine from '@/webgl/audio/AudioEngine'
import Sound from '@/webgl/audio/Sound'
import { RenderContext } from '@/webgl/core/RenderContext'
import Signal from '@/webgl/core/Signal'
import { clamp01 } from '@/webgl/math'
import { BonusType, createBonusEntity } from '@/webgl/runner/entities/BonusEntity'
import { CollectibleEntity, CollectibleType, CollectStatus } from '@/webgl/runner/entities/CollectibleEntity'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import ICollectibleTrack from '@/webgl/runner/interfaces/ICollectibleTrack'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import { getPlaySequence as getSequenceConfig } from '@/webgl/runner/scenarios'
import { ScenarioSetup } from '@/webgl/runner/scenarios/ScenarioSetup.types'
import { createSequenceCompletion } from '@/webgl/runner/scenarios/SequenceCompletion'
import { SequenceAlternativeMode, SequenceConfig } from '@/webgl/runner/scenarios/SequenceConfig.types'
import GateTrack from '@/webgl/runner/scenarios/tracks/GateTrack'
import ProceduralTrack from '@/webgl/runner/scenarios/tracks/ProceduralTrack'
import ScrollSystem from '@/webgl/runner/systems/ScrollSystem'
import { IGameSegment } from '@/webgl/runner/systems/SequencingSystem'

class Obstacle {
  time: number = 0
  duration: number = 2
  releaseDelay: number = 1.0
  get completed(): boolean {
    return this.time > this.duration
  }
  get released(): boolean {
    return this.time > this.duration + this.releaseDelay
  }
  constructor(readonly id: string) {}
  update(time: TimeEntity) {
    this.time += time.deltaTime
  }
}

export class PlaySegment implements IGameSegment {
  world: GameWorld
  config: SequenceConfig
  renderer: IRunnerRenderer

  onComplete = new Signal<void>()
  completed: boolean = false
  resolved: boolean = false

  track: ICollectibleTrack
  obstacle: Obstacle = null

  grabbedCount: number = 0
  spawnedCount: number = 0

  activeSound: Sound | null = null
  backgroundAudio: Sound | null = null
  audioPreviousTime: number = 0
  audioGrabThreshold: number = 3

  completeTimer: number = 0
  completeDelay: number = -1
  alwaysWrong: boolean = false

  constructor(
    readonly id: string,
    readonly subsegment: IGameSegment = null
  ) {}

  // #region Helpers

  isGrabEnabled(): boolean {
    return (
      !this.completed && this.world.player.grabDisabled !== true && import.meta.env.VITE_RUNNER_DISABLE_GRAB !== 'true'
    )
  }

  isCompleteConditionMet(): boolean {
    const completion = this.world.state.value.completion
    const currentBlock = this.world.state.getCurrentCompletionBlock()
    const blockIndex = completion.currentBlock
    const blockCount = completion.blocks.length

    const condition = this.config.condition
    if (!condition) return currentBlock.status != CollectStatus.Neutral

    switch (condition.id) {
      case 'collected-count':
        return currentBlock.collected.length >= condition.count

      case 'spawned-count':
        return this.spawnedCount >= condition.count && this.obstacle == null

      case 'grab-count':
        return this.grabbedCount >= condition.count

      case 'blocks':
        return blockIndex == blockCount - 1 && currentBlock.status != CollectStatus.Neutral
    }
  }

  isGoal(id: string): boolean {
    const goal = this.world.state.getCurrentGoal()
    let isGoal: boolean

    const currentBlock = this.world.state.getCurrentCompletionBlock()

    switch (this.config.alternativeMode) {
      case SequenceAlternativeMode.Shopping:
      case SequenceAlternativeMode.Unordered:
        isGoal = currentBlock.goal.includes(id)
        break
      case SequenceAlternativeMode.ShoppingBalance:
        isGoal = true
        break
      default:
        isGoal = id == goal
        break
    }
    return isGoal
  }
  // #endregion

  // #region Segment Lifecycle
  init(world: GameWorld, renderer: IRunnerRenderer, setup: ScenarioSetup): void {
    this.renderer = renderer
    this.world = world
    this.config = getSequenceConfig(setup, this.id)
    this.subsegment?.init(world, renderer, setup)
    this.completeDelay = -1

    const trackCfg = this.config.track
    switch (trackCfg.type) {
      case 'procedural':
        this.track = new ProceduralTrack(this.world, trackCfg)
        break
      case 'gate-track':
        this.track = new GateTrack(this.world, trackCfg)
        break
    }
  }

  async load(): Promise<void> {
    if (this.subsegment) await this.subsegment.load()
  }

  start(): void {
    this.world.systems.get<ScrollSystem>(ScrollSystem).enable()
    this.completed = false

    if (!this.world.state.value.completion) {
      const completion = createSequenceCompletion(this.config)
      this.world.state.setCompletion(completion)
    }

    this.world.background.spawnableItems = this.config.scrolling

    this.world.events.CollectibleGrabbed.on(this.collectibleGrabbed)
    this.world.events.CollectibleMissed.on(this.collectibleMissed)

    this.grabbedCount = 0

    this.subsegment?.start()

    if (this.config.audio && this.config.audio.music) AudioEngine.instance.music.play(this.config.audio.music)

    const startOptions = this.config.start
    if (!startOptions) return

    if (startOptions.audio) this.activeSound = AudioEngine.play(startOptions.audio)

    if (startOptions.backgroundAudios?.length) this.playBackgroundAudio(startOptions.backgroundAudios, 0)

    if (startOptions.bonus) this.world.addBonus(createBonusEntity(startOptions.bonus))

    if (startOptions.alterJauge) this.world.state.alterJauge(startOptions.alterJauge)

    if (startOptions.setJauge) this.world.state.setJauge(startOptions.setJauge)

    if (startOptions.barrier) {
      this.world.events.SpawnCollectible.emit({
        id: startOptions.barrier,
        lane: -1,
      })
      this.world.events.SpawnCollectible.emit({
        id: startOptions.barrier,
        lane: 0,
      })
      this.world.events.SpawnCollectible.emit({
        id: startOptions.barrier,
        lane: 1,
      })
    }
  }

  stop(): void {
    const stopOptions = this.config.stop
    if (stopOptions) {
      if (stopOptions.alterJauge) this.world.state.alterJauge(stopOptions.alterJauge)
    }

    this.releaseObstacle()
    this.world.events.CollectibleGrabbed.off(this.collectibleGrabbed)
    this.world.events.CollectibleMissed.off(this.collectibleMissed)
    if (!this.config.stop?.preserveCompletion) this.world.state.setCompletion(null)
    this.backgroundAudio?.stop()
    this.backgroundAudio = null
    this.activeSound?.stop()
    this.activeSound = null
    this.subsegment?.stop()
  }
  //#endregion

  //#region Miss
  collectibleMissed = (collectible: CollectibleEntity) => {
    if (!this.isGrabEnabled()) return

    const settings = this.world.settings
    const canFail = this.config.condition && this.config.condition.id == 'blocks'

    if (this.isGoal(collectible.data.id)) {
      this.world.state.alterJauge(settings.MISS_INCR_AMOUNT)
      if (canFail) this.world.state.failCurrentGoal()
    }

    this.OnCollectibleMissed(collectible)
  }
  //#endregion

  //#region Grab
  collectibleGrabbed = (collectible: CollectibleEntity) => {
    if (!this.isGrabEnabled()) return

    const id = collectible.data.id
    const settings = this.world.settings
    const isGoal = this.isGoal(id)
    const isBonus = collectible.data.bonus !== undefined
    const isObstacle = collectible.data.type == CollectibleType.Obstacle
    const isChill = collectible.data.chill
    const canFail = this.config.condition && this.config.condition.id == 'blocks'

    const helmet = this.world.isBonusActive(BonusType.Helmet)
    if (helmet && (!isBonus || collectible.data.bonus === BonusType.Helmet)) return

    this.grabbedCount += 1

    if (isObstacle) {
      this.obstacle = new Obstacle(id)
      this.world.state.setObstacle(this.obstacle.id)
      if (!isChill) this.world.state.alterJauge(settings.OBSTACLE_INCR_AMOUNT)
    }

    if (isBonus) {
      AudioEngine.play(SFXAudio.SpecialItem)
      this.world.addBonus(createBonusEntity(collectible.data.bonus))
    }

    if (isGoal) {
      AudioEngine.play(SFXAudio.GoodItem)
      this.world.state.collectItem(id)
      this.world.state.alterJauge(-settings.GOOD_DECR_AMOUNT)
      this.OnValidCollectible(collectible)
    }

    if (!isGoal && !isBonus && !isObstacle && canFail) {
      this.world.state.failCurrentGoal()
    }

    if (!isGoal && collectible.data.type == CollectibleType.Standard) {
      AudioEngine.play(SFXAudio.BadItem)
      this.world.state.alterJauge(settings.WRONG_INCR_AMOUNT)
    }

    collectible.collected = true
    collectible.collectStatus = isGoal || isBonus ? CollectStatus.Succeed : CollectStatus.Failed
    collectible.collectedTime = collectible.lifetime

    const mask = this.world.isBonusActive(BonusType.Mask)
    if (mask) collectible.collectStatus = CollectStatus.Succeed

    if (collectible.priceID !== undefined && collectible.priceID !== 0) {
      collectible.collectStatus = CollectStatus.Failed
      this.world.state.alterJauge(settings.WRONG_INCR_AMOUNT * 2.0)
    }

    const audio = this.playAudioForCollectible(collectible)
    if (isObstacle && audio) {
      this.obstacle.duration = audio.duration
    }

    if (collectible.data.type == CollectibleType.TurningPoint) {
      this.track.clear()
      this.completeSequence()
    }

    this.OnCollectibleGrabbed(collectible)

    this.world.events.PlayCollect.emit({
      isGoal,
      collectible,
    })
  }
  //#endregion

  //#region Background Audio
  playBackgroundAudio(
    audios: {
      audio: string
      delay?: number
    }[],
    index: number
  ) {
    const delay = (audios[index].delay || 0) * 1000
    setTimeout(() => {
      const hasNext = audios.length > index + 1
      this.backgroundAudio = AudioEngine.play(audios[index].audio)

      // AudioEngine.play returns null when the source is unavailable. Skip
      // straight to the next track so a single missing/unloaded source does
      // not silently break the rest of the chain.
      if (!this.backgroundAudio) {
        console.warn('PlaySegment: background audio unavailable, skipping', audios[index].audio)
        if (hasNext) this.playBackgroundAudio(audios, index + 1)
        return
      }

      if (hasNext) {
        this.backgroundAudio.onEnded.on(() => this.playBackgroundAudio(audios, index + 1))
      }
    }, delay)
  }
  //#endregion

  //#region Audio
  playAudioForCollectible(collectible: CollectibleEntity): Sound {
    if (collectible.data.type == CollectibleType.TurningPoint || this.isCompleteConditionMet()) return

    const audio = collectible.data.audio
    const status = collectible.collectStatus

    const interrupt = status == CollectStatus.Succeed || audio !== undefined

    if (interrupt) {
      this.activeSound?.stop()
      this.activeSound = null
    }

    const playGuard = this.audioPreviousTime + this.audioGrabThreshold > this.world.time.unscaledTime

    if (!interrupt && playGuard) {
      return
    }

    if (audio) {
      this.activeSound = AudioEngine.play(audio)
      this.audioPreviousTime = this.world.time.unscaledTime
      return this.activeSound
    }

    if (this.config.audio === undefined) return

    const key = status == CollectStatus.Succeed ? 'good' : 'wrong'
    const interject = this.config.audio[key]
    if (!interject) return

    const src = Array.isArray(interject) ? interject[Math.floor(Math.random() * interject.length)] : interject
    this.audioPreviousTime = this.world.time.unscaledTime
    this.activeSound = AudioEngine.play(src)
    return this.activeSound
  }
  //#endregion

  //#region Resolution
  executeResolution() {
    if (this.resolved) return
    this.resolved = true

    const resolution = this.config.resolution
    this.track.clear()

    /** Default */
    if (!resolution) {
      this.completeSequence()
      return
    }

    switch (resolution.id) {
      case 'delay':
        this.completeTimer = 0
        this.completeDelay = resolution.duration
        break

      case 'barrier':
        this.world.events.SpawnCollectible.emit({ id: resolution.barrier, offset: -1, lane: -1 })
        this.world.events.SpawnCollectible.emit({ id: resolution.barrier, offset: -1, lane: 0 })
        this.world.events.SpawnCollectible.emit({ id: resolution.barrier, offset: -1, lane: 1 })
        break
    }
  }

  completeSequence() {
    this.completed = true

    if (!this.config.stop?.preserveCollectibles) {
      const collectibles = this.world.collectibles
      for (let i = collectibles.length - 1; i >= 0; i--) {
        if (collectibles[i].collected) continue
        this.world.destroyCollectible(collectibles[i])
      }
    }

    if (!this.config.stop?.preserveBonus) this.world.destroyAllBonuses()

    if (!this.config.stop?.preserveCompletion) this.world.state.setCompletion(null)
    this.onComplete.emit(null)
  }
  // #endregion

  releaseObstacle() {
    if (this.obstacle != null) {
      this.obstacle = null
      this.world.state.setObstacle(null)
      this.world.player.grabDisabled = false
    }
  }

  //#region Update

  /** Handle obstacle layout */
  updateObstacle(time: TimeEntity) {
    if (!this.obstacle) return
    const { player } = this.world
    player.grabDisabled = true

    this.obstacle.update(time)

    if (this.obstacle.completed) {
      this.world.state.setObstacle(null)
    }

    if (this.obstacle.released) {
      this.releaseObstacle()
    }
  }

  /** Advance track and spawn items if needed */
  updateTrack(time: TimeEntity) {
    const { settings } = this.world
    const spawnQueue = this.track.evaluate(time.unscaledDeltaTime * settings.scrollSpeed)
    for (const trackItem of spawnQueue) {
      if (trackItem.data.type == CollectibleType.Obstacle && this.obstacle) continue

      this.spawnedCount += 1
      this.world.events.SpawnCollectible.emit({
        id: trackItem.data.id,
        lane: trackItem.lane,
        entity: trackItem.entity,
        priceID: trackItem.priceID,
      })
    }
  }

  /** Increase / Decrease jauge according to jaugeRate settings */
  updatePassiveJauge(time: TimeEntity) {
    if (!this.config.jaugeRate) return
    this.world.state.alterJauge(this.config.jaugeRate * time.unscaledDeltaTime)
  }

  /** Check and apply resolution / completion of the segment */
  updateResolve(time: TimeEntity) {
    if (this.completed || this.resolved) return
    if (this.isCompleteConditionMet()) this.executeResolution()
  }

  /** Handle complete resolution delay */
  updateCompleteTimer(time: TimeEntity) {
    if (!this.resolved || this.completeDelay < 0) return

    const { settings } = this.world
    this.completeTimer += time.unscaledDeltaTime * clamp01(settings.scrollSpeed)

    if (this.completeTimer >= this.completeDelay) {
      this.completeTimer = 0
      this.completeDelay = -1
      this.completeSequence()
    }
  }

  update(time: TimeEntity): void {
    this.subsegment?.update?.(time)
    this.updateObstacle(time)
    this.updateTrack(time)
    this.updatePassiveJauge(time)
    this.updateResolve(time)
    this.updateCompleteTimer(time)
  }

  render(context: RenderContext) {
    this.subsegment?.render?.(context)
  }

  //#endregion

  // #region Abstract
  OnValidCollectible(collectible: CollectibleEntity) {}

  OnCollectibleGrabbed(collectible: CollectibleEntity) {}

  OnCollectibleMissed(collectible: CollectibleEntity) {}
  // #endregion
}
